import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../../utilities/axios';

import * as API_END_POINT from '../../utilities/constant';
import { deliveryEstimate, leadDays } from '../../utilities/deliveryEstimate';
import { fetchProductsWithFallback } from '../../utilities/apiHelper';
import useProductData from '../../hooks/useProductData';
import useProductOptions from '../../hooks/useProductOptions';
import { useStore } from '../../context/StoreContext';
import CartView from '../../components/CartView';
import {
  Button,
  ProductCard,
  ProductDetailView,
  ProductDetailViewSkeleton,
  ProductRail,
} from '../../shared/components';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';
import '../../shared/components/organisms/Browse/browse.css';
import logoPng from '../../assets/images/logo-png.png';

/* /product/:productId — SP-main's product page.

   Presentation moved, machinery did not: `useProductOptions` still resolves the
   variant, price and stock, `useProductData` still fetches, and the cart payload
   keeps its shape. The markup is now `.pp-*` in a shared organism, the same
   split `/all-products` made.

   No `PageLayout`: it wraps children in `max-w-7xl mx-auto` and renders its own
   breadcrumb, while the reference's `.br-wrap.pp-wrap` is 1240px with a 40px
   gutter and owns the crumb. Header and footer come from `App.js`.

   `src/components/ProductInfo` is no longer this route's view; it is left in
   place, unimported. */

/* Shopify's placeholder for "no real options" is a single axis whose only value
   is the string below. Not a choice, so not drawn. */
const DEFAULT_TITLE = 'Default Title';
const OPTION_KEYS = ['option1', 'option2', 'option3'];

/* Some feeds deliver `body_html` entity-encoded (`&lt;p&gt;`), which would show
   the shopper markup instead of words. ⛔ Do not simplify away — without it
   every WooCommerce-sourced item is a wall of tags. */
const decodeDesc = (html) => {
  if (!html) return '';
  if (html.indexOf('&lt;') === -1) return html;
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.textContent;
};

/* youtu.be, /watch?v=, /embed/, /shorts/ and vimeo.com/<id>. Copied from the
   previous view: it already handles the shapes brands actually paste in. */
const embedUrl = (url) => {
  if (!url) return '';
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return url;
};

const ProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { storeDetails, addToCart, cartItems, pageDetails } = useStore();
  const { productData, loading, error, isNotFound } = useProductData(productId);

  const {
    selectedOptions,
    allOptions,
    handleOptionChange,
    selectedVariant,
    isProductUnavailable,
    getDiscountPercentage,
    isOptionAvailable,
    isOptionPublished,
    allOptionsSelected,
  } = useProductOptions(productData, storeDetails);

  const [quantity, setQuantity] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [productFilters, setProductFilters] = useState(null);
  const [related, setRelated] = useState([]);

  const details = productData?.productDetails;

  /* The partner owns the skin, and this route never asked for it. `.sfx`
     resolves the static tokens, but `--o` (anything clickable) and `--o-ink`
     are written on the element at run time from the saved Style config.

     ⛔ This is why Add to cart rendered as a borderless text link:
     `.sf-btn-primary` is `background: var(--o); color: var(--o-ink)`, and with
     neither token defined both declarations are dropped. The same gap silently
     killed `.pp-thumb.on`'s ring, the `.pp-trust` glyphs and the accordion
     hover. `/all-products` and `/brands/:id` already do this. */
  const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);

  useEffect(() => {
    let live = true;
    axios.post(`${API_END_POINT.PRODUCT_FILTERS}`, {})
      .then((res) => { if (live) setProductFilters(res.data.data?.availableFilters); })
      .catch(() => { /* the crumb falls back to "All products" — not worth a toast */ });
    return () => { live = false; };
  }, []);

  useEffect(() => { setQuantity(1); }, [selectedVariant?.id]);

  /* ---- variant availability ------------------------------------------------
     Carried over unchanged, including the WooCommerce separator: a variant's
     title joins option values with " / " on Shopify and ", " on WooCommerce,
     and getting it wrong silently disables every second and third option. */
  const onlyAvailableVariant = useCallback((values, optionKey) => {
    if (optionKey === 'option1') return values;
    const variants = (details?.product_variants || [])
      .filter((v) => v.status === 1 || v.status === '1')
      .map((v) => v.title);
    const sep = details?.user?.shop_detail?.[0]?.channel === 'woocommerce' ? ', ' : ' / ';
    return values.filter((val) => {
      const search = optionKey === 'option2'
        ? `${selectedOptions.option1}${sep}${val}`
        : `${selectedOptions.option1}${sep}${selectedOptions.option2}${sep}${val}`;
      return variants.some((t) => t.includes(search));
    });
  }, [details, selectedOptions.option1, selectedOptions.option2]);

  /* When a wider option changes, a narrower one may no longer exist. Both
     effects are the previous view's, unchanged. */
  useEffect(() => {
    if (!selectedOptions.option1) return;
    const avail = onlyAvailableVariant(allOptions.option2 || [], 'option2');
    if (!avail.includes(selectedOptions.option2)) {
      handleOptionChange('option2', avail[0]);
      handleOptionChange('option3', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions.option1]);

  useEffect(() => {
    if (!selectedOptions.option1 || !selectedOptions.option2) return;
    const avail = onlyAvailableVariant(allOptions.option3 || [], 'option3');
    if (!avail.includes(selectedOptions.option3)) {
      handleOptionChange('option3', avail[0] || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions.option1, selectedOptions.option2]);

  const options = useMemo(() => {
    if (!details?.product_options) return [];
    const named = [...details.product_options].sort((a, b) => a.sequence - b.sequence);
    return OPTION_KEYS.map((key, i) => {
      const name = named[i]?.name;
      const values = allOptions?.[key] || [];
      if (!name || values.length === 0) return null;
      if (values[0] === DEFAULT_TITLE) return null;
      const usable = onlyAvailableVariant(values, key).filter((v) => isOptionPublished(key, v));
      if (usable.length === 0) return null;
      return {
        key,
        name,
        onSelect: (v) => handleOptionChange(key, v),
        values: usable.map((v) => ({
          value: v,
          selected: selectedOptions[key] === v,
          disabled: !isOptionAvailable(key, v),
        })),
      };
    }).filter(Boolean);
  }, [details, allOptions, selectedOptions, onlyAvailableVariant, isOptionAvailable, isOptionPublished, handleOptionChange]);

  /* A vendor and a brand are not the same id: on a multi-brand store the
     display name and the link both come from `shopify_vendor.shopdot_vendor`,
     not `brand_details`. */
  const brand = useMemo(() => {
    if (!details) return null;
    const bd = details.user?.brand_details || {};
    const vendor = details.shopify_vendor?.shopdot_vendor;
    const isMulti = !!details.user?.is_multi_brand;
    const href = bd.id
      ? (isMulti && vendor?.id ? `/brands/${bd.id}/vendor/${vendor.id}` : `/brands/${bd.id}`)
      : null;
    const promo = bd.brand_promo;
    return {
      name: isMulti ? vendor?.display_name : bd.store_name,
      href,
      logo: bd.profile_picture,
      story: vendor?.about_description || bd.brand_story,
      community: bd.charitable_or_community_prog,
      values: (details.user?.brand_values || [])
        .map((bv) => bv?.store_values?.name)
        .filter(Boolean),
      returns: details.user?.brand_retailer_preference?.policy_description,
      videoUrl: promo,
      embedUrl: embedUrl(promo),
    };
  }, [details]);

  const shipping = useMemo(() => {
    if (!details) return null;
    const address = leadDays(details.user?.brand_details?.shipping_rate?.shipping_address)
      ? details.user.brand_details.shipping_rate.shipping_address
      : details.user?.shipping_address;
    return {
      timeLabel: deliveryEstimate(address),
      cost: details.user?.brand_details?.shipping_rate?.shipping_cost || 0,
    };
  }, [details]);

  /* The product's own category wins; the `?category=` the shopper arrived
     with is the fallback, since a listing's filter can name a category the
     product isn't in. Despite their names, `category_name` /
     `subcategory_name` carry codes (the tag value), resolved to display names
     against the facet list. A code missing from the facets yields no Category
     row and no crumb, rather than a raw code linking to an empty listing. */
  const category = useMemo(() => {
    const resolve = (code, subCode) => {
      const cat = code && productFilters?.categories?.find((c) => c.code === code);
      if (!cat) return null;
      const sub = cat.subCategories?.find((sc) => sc.code === subCode);
      return { name: cat.name, code: cat.code, sub: sub?.name || null, subCode: sub?.code || null };
    };
    return resolve(details?.category_name, details?.subcategory_name)
      || resolve(searchParams.get('category'), searchParams.get('subcategory'));
  }, [searchParams, productFilters, details?.category_name, details?.subcategory_name]);

  const categoryHref = category
    ? `/all-products?category=${encodeURIComponent(category.code)}`
    : null;

  /* The reference's trail is Home / Shop All / {category} / {product}. Ours
     said "All products" and stopped before the product name, so it never named
     the page you were on. The last crumb is plain text, because it IS this
     page. */
  const crumb = useMemo(() => (
    <>
      <Link to="/">Home</Link> / <Link to="/all-products">Shop All</Link>
      {categoryHref && <> / <Link to={categoryHref}>{category.name}</Link></>}
      {details?.title && <> / {details.title}</>}
    </>
  ), [category, categoryHref, details]);

  /* ---- Product details, the reference's `detailsBody()` --------------------
     Brand and Category only. Shipping and returns are stated once, in the list
     under Add to cart. */
  const specs = useMemo(() => {
    if (!details) return [];
    const rows = [];

    if (brand?.name) {
      rows.push({
        label: 'Brand',
        value: brand.href ? <Link to={brand.href}>{brand.name}</Link> : brand.name,
      });
    }

    if (category && categoryHref) {
      rows.push({
        label: 'Category',
        value: (
          <>
            <Link to={categoryHref}>{category.name}</Link>
            {category.sub && <> / {category.sub}</>}
          </>
        ),
      });
    }

    return rows;
  }, [details, brand, category, categoryHref]);

  /* ---- "You might also like" -----------------------------------------------
     The user's ranking, not the reference's: same brand AND same category
     first, then same brand — so the rail never leaves the brand, where the
     reference's would pad with other brands.

     One endpoint, the brand's own `/store/brands/:id/products`, which
     `/brands/:id` already uses; a multi-brand store's vendor has its own
     endpoint and id, and using the wrong one fills the rail with another
     vendor's products.

     Six, to keep the bottom of a product page light. Failure is silent: a
     missing suggestion row is not worth a visible error here. */
  const brandId = details?.user?.brand_details?.id;
  const vendorId = details?.user?.is_multi_brand ? details?.shopify_vendor?.shopdot_vendor?.id : null;
  const railSubCode = category?.subCode || searchParams.get('subcategory') || null;

  useEffect(() => {
    if (!brandId || !details?.id) { setRelated([]); return; }
    let live = true;

    /* The same call the browse grid makes. Posting straight to
       `/store/brands/:id/products` with a hand-rolled body left the rail empty;
       `fetchProductsWithFallback` is what `/all-products` uses, so the request
       shape, status filters and row shape are already proven against this API.

       The order is the server's: "same brand AND same category" is its own
       query with the subcategory filter, "same brand, any category" a second
       one. Ranking client-side meant guessing which field carries the
       subcategory, and guessing wrong is silent. */
    const brandFilter = vendorId
      ? { field: 'vendors', options: { operator: 'in', values: [String(vendorId)] } }
      : { field: 'brand', operator: 'in', value: [String(brandId)] };

    const base = [
      { field: 'status', operator: 'in', value: ['1'] },
      { field: 'shopify_status', operator: 'in', value: ['active'] },
      brandFilter,
    ];

    const ask = (filter) => fetchProductsWithFallback({
      paging: { limit: 12, offset: 0 },
      sort: [{ fieldName: 'created_at', fieldOrder: 'DESC' }],
      filter,
    })
      .then((r) => r?.data?.data?.items || [])
      .catch(() => []);

    /* Without a subcategory there is no "same category" pass to make, so brand
       order stands. */
    const sameCat = railSubCode
      ? ask([...base, { field: 'subcategory', options: { operator: 'in', values: [String(railSubCode)] } }])
      : Promise.resolve([]);

    Promise.all([sameCat, ask(base)]).then(([first, rest]) => {
      if (!live) return;
      const mine = String(details.id);
      const seen = new Set([mine]);
      const out = [];
      [...first, ...rest].forEach((it) => {
        const k = String(it?.id);
        if (!k || seen.has(k)) return;
        seen.add(k);
        out.push(it);
      });
      setRelated(out.slice(0, 6));
    });

    return () => { live = false; };
  }, [brandId, vendorId, details?.id, railSubCode]);

  /* The payload shape is the server's and is unchanged. The one difference is
     that `quantity` is the stepper's value instead of a hardcoded 1, and an
     item already in the cart gains that many. */
  const inCartLine = useCallback(() => {
    let hit = null;
    (cartItems || []).forEach((group) => {
      (group?.cart_items || []).forEach((line) => {
        if (line?.product_variants?.id === selectedVariant?.id) {
          hit = { group, line };
        }
      });
    });
    return hit;
  }, [cartItems, selectedVariant]);

  const handleAddToCart = () => {
    if (!selectedVariant) return;
    setAdding(true);
    const existing = inCartLine();
    const body = existing
      ? {
        brands: [{
          brand_user_id: existing.group?.brand_user?.id,
          products: [{
            product_id: existing.line?.products?.id,
            variants: [{
              variant_id: existing.line?.product_variants?.id,
              quantity: (existing.line.cart_quantity || 0) + quantity,
            }],
            customizableNote: existing.line?.customizable_note,
          }],
        }],
      }
      : {
        brands: [{
          brand_user_id: details?.user?.id,
          products: [{
            product_id: details?.id,
            variants: [{ variant_id: selectedVariant?.id, quantity }],
            /* Always null today: `is_customizable` reaches the payload but no
               note input is rendered. Kept wired because the server reads it. */
            customizableNote: null,
          }],
        }],
      };
    addToCart(body);
    setCartOpen(true);
    setTimeout(() => setAdding(false), 1500);
  };

  const unavailable = isNotFound || (!loading && !error && !details);

  if (loading) {
    return (
      <div ref={sfxRef} className="sfx">
        <ProductDetailViewSkeleton />
      </div>
    );
  }

  if (error && !isNotFound) {
    return (
      <div className="br-wrap pp-wrap">
        <nav className="br-crumb" aria-label="Breadcrumb">{crumb}</nav>
        <div className="br-empty">
          <h2>Something went wrong</h2>
          <p>We couldn&rsquo;t load this product right now. Please try again later.</p>
          <Button href="/all-products" variant="ghost">Continue shopping</Button>
        </div>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="br-wrap pp-wrap">
        <nav className="br-crumb" aria-label="Breadcrumb">{crumb}</nav>
        <div className="br-empty">
          <h2>Product no longer available</h2>
          <p>It may be out of stock, or no longer carried by this store.</p>
          <Button href="/all-products" variant="ghost">Continue shopping</Button>
        </div>
      </div>
    );
  }

  const priceInfo = getDiscountPercentage();

  return (
    <div ref={sfxRef} className="sfx">
      <ProductDetailView
        product={details}
        description={decodeDesc(details?.body_html)}
        specs={specs}
        crumb={crumb}
        price={`$${priceInfo.originalPrice}`}
        options={options}
        brand={brand}
        shipping={shipping}
        outOfStock={isProductUnavailable()}
        outOfStockLabel={allOptionsSelected ? 'Selected variant is out of stock' : 'Out of stock'}
        quantity={quantity}
        onQuantityChange={setQuantity}
        onAddToCart={handleAddToCart}
        adding={adding}
        fallbackImage={logoPng}
        variantImage={selectedVariant?.image}
        related={(
          <ProductRail
            title="You might also like"
            seeAllLabel="Shop all"
            seeAllHref="/all-products"
            items={related}
            renderItem={(item) => {
              /* The same field access as the browse grid (Products/index.js's
                 `renderItem`): a vendor's display name and a brand's name are
                 different fields, and `price_msrp` is already formatted. */
              const images = Array.isArray(item.product_images) ? item.product_images : [];
              const main = [...images].sort((a, b) => Number(b.is_main) - Number(a.is_main))[0];
              const isVendor = item?.is_multi_brand;
              const itemBrand = isVendor
                ? item.vendor?.name || item.shopify_vendor?.shopdot_vendor?.display_name
                : item.brand?.name || item.brand_name;

              return (
                <ProductCard
                  key={item.id}
                  name={item.title}
                  brand={itemBrand || brand?.name}
                  price={item.price_msrp}
                  image={main?.src}
                  href={`/product/${item.id}`}

                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                    e.preventDefault();
                    navigate(`/product/${item.id}`);
                  }}
                  objectFit="contain"
                />
              );
            }}
          />
        )}
      />
      {/* The drawer's contract is `isOpen` / `onClose` and is not this page's to
          change — the cart is being redesigned behind these props. */}
      <CartView isOpen={cartOpen} onClose={() => setCartOpen(false)} loading={adding} />
    </div>
  );
};

export default ProductPage;
